<script>
import { addMonths, addMinutes, setHours, setMinutes, startOfDay } from 'date-fns';
import { slide } from 'svelte/transition';
import Calendar from './Calendar.svelte';
import TimePicker from './TimePicker.svelte';
import { today, minute, getNow } from '../data/datetime';
import { getUser } from '../data/user';
import { router } from '../libs/router';
import { uuid } from '../libs/uuid';
import { updateSprint } from '../data/sprints';
import { createEditor } from '../editor';
import { tooltipTop } from '../tooltip';
import globals from '../globals';
import { storedSprints, deleteSprint } from '../data/sprints';

const privateTip = `Only those with the link can join this sprint.`;

const ERRORS = {
  DATA_REQUIRED: 'The request was empty.',
  INVALID_START_MINUTE: 'The start time for this sprint is invalid.',
  INVALID_END_MINUTE: 'The end time for this sprint is invalid.',
  NOT_FOUND: 'This could not be found.',
  FORBIDDEN: 'You do not have permission to do that.',
  AUTHORIZATION_REQUIRED: 'Your account was not created successfully. Refresh and try again.',
};

export let sprintId;
let promptElement;
let promptEditor;

$: sprint = $storedSprints && storedSprints.getSprint(sprintId);

$: day = sprint && startOfDay(new Date(sprint.start));
$: start = sprint && new Date(sprint.start);
$: end = sprint && new Date(sprint.end);
$: duration = sprint && (sprint.end - sprint.start) / 1000 / 60;
$: start = getTimeFromDay(day);
$: end = addMinutes(start, duration);
$: titleValue = sprint && sprint.title || '';
$: listed = sprint && sprint.listed;
$: createPromptEditor(promptElement)
$: sprint && promptEditor && sprint.prompt && promptEditor.setContents(sprint.prompt);

$: listed = listed ? 'Listed ' : '';
$: unit = duration < 60 ? 'Minute' : 'Hour';
$: value = duration < 60 ? duration : duration / 60;
$: sprintTitle = titleValue || `A ${value} ${unit} Group Sprint`;


function createPromptEditor(root) {
  if (promptEditor) promptEditor.view.destroy();
  if (root) promptEditor = createEditor(root);
}

async function saveEdits() {
  localStorage.lastDuration = duration;
  const creator = (await getUser()).uid;
  let text = titleValue.trim();
  const title = text ? text : undefined;
  text = promptEditor.getText().trim();
  const prompt = text ? promptEditor.getContents() : undefined;

  try {
    const updates = { ...sprint, start: start.getTime(), end: end.getTime(), creator, title, prompt, listed };
    sprint = await updateSprint(updates);
    globals.inform('success', 'Sprint saved', 'successfully.');
    router.navigate(sessionStorage.lastUrl || `/sprints/${sprint.id}`);
  } catch (err) {
    globals.alert('Error', ERRORS[err.message] || err.message);
  }
}

function cancel() {
  router.navigate(sessionStorage.lastUrl || `/sprints/${sprint.id}`);
}

async function deleteTheSprint() {
  if (!await globals.confirm('Are you sure?', 'Absolutely positively delete?')) return;
  globals.inform('info', 'Deleting...', 'all sprint resources.');
  await deleteSprint(sprint.id);
  globals.inform('success', 'Sprint deleted', 'for all time.');
  router.navigate('/');
}

function getTimeFromDay(day) {
  if (!day || !start) return;
  const hours = start.getHours();
  const minutes = start.getMinutes();
  return setMinutes(setHours(day, hours), minutes);
}

</script>

<section>
	<div class="creation">
    <h2>{sprintTitle}</h2>
		<div class="box">
      <div class="editor">
        {#if sprint}
        <div class="group-sprint" transition:slide|local={{ duration: 200 }}>
          <div class="form-group">
            <input type="text" class="form-control" placeholder="Title (optional)" bind:value={titleValue}/>
          </div>
          <div class="form-group">
            <div class="prompt form-control" bind:this={promptElement} placeholder="Writing prompt (optional)"></div>
          </div>
          <div class="date-time">
            <div class="calendar">
              <div class="time-label">on</div>
              <div class="form-group">
                <Calendar bind:selected={day} min={$today} max={addMonths($today, 1)}/>
              </div>
              <button class="btn link delete" on:click={deleteTheSprint}>Delete sprint</button>
            </div>
            <div class="time">
              <div class="time-label">at</div>
              <div class="form-group">
                <TimePicker min={addMinutes($minute, 1)} bind:selected={start} interval={5}/>
              </div>
              <div class="time-label">for</div>
              <div class="form-group">
                <select class="form-control" bind:value={duration}>
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={20}>20 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                </select>
              </div>
              <div class="time-label">will be</div>
              <div class="form-group">
                <label class="form-check-label" for="listed-check" use:tooltipTop={privateTip}>
                  <i class="icon icon-checkbox-{listed ? 'marked' : 'blank'}-circle-outline"></i>
                  <input class="form-check-input" type="checkbox" id="listed-check" bind:checked={listed}>
                  Publicly Listed
                </label>
                <div class="hint">Hint: Share the sprint’s link for others to join.</div>
              </div>
              <div class="spacer"></div>
              <button class="btn primary outline fill" on:click={cancel}>Cancel</button>
              <button class="btn primary fill" on:click={saveEdits}>Save Changes</button>
            </div>
          </div>
        </div>
        {/if}
      </div>
    </div>
  </div>
</section>

<style>
.creation {
	max-width: 660px;
}
.box {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	background: var(--white);
	box-shadow: 0 1px 3px 0 rgba(0,0,0,0.20), 0 2px 4px 0 rgba(0,0,0,0.20);
	border-radius: 5px;
	padding: 48px;
}
.editor {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.group-sprint {
  display: flex;
  flex-direction: column;
}
.date-time {
  display: flex;
}
.calendar {
  margin-right: 32px;
}
.time {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
}
.spacer {
  flex: 1;
}
.btn.fill {
  align-self: stretch;
  margin-bottom: 16px;
}
.btn.delete {
  font-size: var(--font-size-100);
  color: var(--warm-grey-400);
}
.time-label {
  font-size: var(--font-size-100);
  color: var(--warm-grey-400);
  margin-bottom: 2px;
}
.form-check-label {
  position: relative;
  width: 100%;
  font-size: var(--font-size-200);
  cursor: pointer;
}
.form-check-label .icon {
  font-size: 24px;
  margin-left: -1.25rem;
  margin-right: .25rem;
  line-height: 0;
  position: relative;
  bottom: -3px;
}
.form-check-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}
.icon-checkbox-blank-circle-outline {
  color: var(--warm-grey-400);
}
.icon-checkbox-marked-circle-outline {
  color: var(--teal-600);
}
.hint {
  margin-top: 8px;
  font-size: var(--font-size-050);
  color: var(--warm-grey-400);
}
@media (max-width: 640px) {
  .date-time {
    flex-direction: column;
  }
}
</style>
